<template>
  <!-- Prompt mot de passe -->
  <div v-if="showPrompt" class="admin-auth-overlay" @keyup.esc="showPrompt = false">
    <div class="admin-auth-box">
      <input
        type="password"
        v-model="adminPassword"
        @keyup.enter="checkPassword"
        placeholder="Mot de passe"
        autofocus
      />
      <button @click="checkPassword">Valider</button>
      <div v-if="authError" class="admin-auth-error">{{ authError }}</div>
    </div>
  </div>

  <!-- Bouton toggle permanent (bas-droite) -->
  <button
    v-if="isAuthenticated"
    class="admin-toggle-btn"
    :class="{ active: isOpen }"
    @click="togglePanel"
    aria-label="Mode admin"
  >⚙</button>

  <!-- Barre admin -->
  <div v-if="isAuthenticated && isOpen" class="bar-mode-admin">
    <div class="admin-buttons">
      <button @click="$emit('reset-to-rag0')">R</button>
      <button @click="activateModeList" :class="{ active: vignetteActive }">Vignettes</button>
      <button @click="toggleHideFiche" :class="{ active: hideFiche }">{{ hideFiche ? 'Large' : 'Small' }}</button>
      <button @click="toggleShowAll" :class="{ active: showAll }">Fiches cachées</button>
      <button @click="toggleModal('datas')" :class="{ active: storeModalMessage === 'datas' }">data</button>
      <button @click="toggleModal('stats')" :class="{ active: storeModalMessage === 'stats' }">stats</button>
      <button @click="showInfo = !showInfo" :class="{ active: showInfo }">i</button>
      <span v-if="showInfo">
        <i>uuid: <b>{{ storeUrlArray.uuid }}</b> | v<b>{{ maVersion }}</b> | <b>{{ resolution }}</b> | <b>{{ dataVersion }}</b></i>
      </span>
      <button @click="logout">Logout</button>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {
  name: 'AdminPanel',
  props: {
    dataVersion: String
  },
  emits: ['reset-to-rag0'],
  data() {
    return {
      isAuthenticated: false,
      isOpen: false,
      showPrompt: false,
      adminPassword: '',
      authError: '',
      showInfo: false,
      vignetteActive: false,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      resizeObserver: null
    };
  },
  computed: {
    ...mapState(['hideFiche', 'showAll', 'storeModalMessage', 'storeUrlArray', 'maVersion']),
    resolution() {
      return `${this.windowWidth}x${this.windowHeight}`;
    }
  },
  mounted() {
    this.isAuthenticated = localStorage.getItem('adminSession') === 'true';
    this.initResizeObserver();
  },
  beforeUnmount() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  },
  watch: {
    isOpen(val) {
      document.title = val
        ? `R agency • Refs • V${this.maVersion} - ${new Date().toISOString().slice(0, 16)}`
        : `R agency • Refs`;
    }
  },
  methods: {
    ...mapActions(['updateStateValue', 'updateStoreUrl']),

    activate() {
      if (this.isAuthenticated) {
        this.togglePanel();
      } else {
        this.showPrompt = true;
      }
    },

    checkPassword() {
      if (this.adminPassword === 'poelvoorde') {
        localStorage.setItem('adminSession', 'true');
        this.isAuthenticated = true;
        this.isOpen = true;
        this.showPrompt = false;
        this.adminPassword = '';
        this.authError = '';
        this.updateStateValue({ key: 'modeAdmin', value: true });
      } else {
        this.authError = 'Mot de passe incorrect';
      }
    },

    togglePanel() {
      this.isOpen = !this.isOpen;
      this.updateStateValue({ key: 'modeAdmin', value: this.isOpen });
      if (!this.isOpen) {
        this.updateStateValue({ key: 'showAll', value: false });
        this.updateStateValue({ key: 'storeModalMessage', value: 'close' });
      }
    },

    logout() {
      localStorage.removeItem('adminSession');
      this.isAuthenticated = false;
      this.isOpen = false;
      this.updateStateValue({ key: 'modeAdmin', value: false });
      this.updateStateValue({ key: 'showAll', value: false });
    },

    activateModeList() {
      if (!this.vignetteActive && this.hideFiche) {
        this.updateStateValue({ key: 'hideFiche', value: false });
      }
      this.vignetteActive = !this.vignetteActive;
      this.updateStateValue({ key: 'triggerModeList', value: Date.now() });
    },

    toggleHideFiche() {
      this.updateStateValue({ key: 'hideFiche', value: !this.hideFiche });
    },

    toggleShowAll() {
      this.updateStateValue({ key: 'showAll', value: !this.showAll });
    },

    toggleModal(name) {
      const next = this.storeModalMessage === name ? 'close' : name;
      this.updateStateValue({ key: 'storeModalMessage', value: next });
    },

    initResizeObserver() {
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => {
          this.windowWidth = window.innerWidth;
          this.windowHeight = window.innerHeight;
        });
        this.resizeObserver.observe(document.body);
      } else {
        window.addEventListener('resize', () => {
          this.windowWidth = window.innerWidth;
          this.windowHeight = window.innerHeight;
        });
      }
    }
  }
};
</script>

<style scoped>
.admin-toggle-btn {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  z-index: 9999;
  background: var(--colorDark);
  color: var(--colorLight);
  font-size: 16px;
  opacity: 0.3;
  transition: opacity 0.2s;
}
.admin-toggle-btn:hover,
.admin-toggle-btn.active {
  opacity: 1;
}

.admin-auth-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
}
.admin-auth-box {
  background: white;
  padding: 24px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
}
.admin-auth-box input {
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 20px;
  font-size: 14px;
  outline: none;
}
.admin-auth-box button {
  padding: 8px 20px;
  border: none;
  border-radius: 20px;
  background: var(--colorDark);
  color: var(--colorLight);
  cursor: pointer;
  font-size: 13px;
}
.admin-auth-error {
  color: #f44336;
  font-size: 12px;
}

.bar-mode-admin {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  width: fit-content;
  height: auto;
  z-index: 10000;
  background: var(--colorDark);
  border-radius: 20px;
  padding: 6px 8px;
  color: var(--colorLight);
}
.admin-buttons {
  display: flex;
  gap: 10px;
  align-items: center;
  white-space: nowrap;
}
.admin-buttons button {
  padding: 6px 12px;
  border: none;
  border-radius: 20px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  background: var(--colorLight);
  color: var(--colorDark);
}
.admin-buttons button:hover {
  opacity: 0.8;
}
.admin-buttons button.active {
  background: #f44336;
  color: white;
}
.admin-buttons span {
  font-size: 11px;
}
</style>
